import { useEffect, useRef } from 'react';
import { mountGoogleButton } from '@wlb/shared/platform/socialAuth';
import { useTheme } from '@wlb/shared/theme';
import type { SocialAuthResult } from '@wlb/shared/types';

interface GoogleSignInButtonProps {
  onResult: (result: SocialAuthResult) => void;
  disabled?: boolean;
}

/**
 * Host for Google's own sign-in button. Google Identity Services renders it
 * into an iframe, so it cannot inherit our button styles — the wrapper only
 * matches its width and vertical rhythm to the sibling social buttons.
 */
export function GoogleSignInButton({ onResult, disabled = false }: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const { isDark } = useTheme();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const mount = () =>
      mountGoogleButton(container, result => onResultRef.current(result), {
        // Keep Google's button in step with our own social buttons.
        theme: isDark ? 'filled_black' : 'outline',
      });
    let cleanup = mount();

    // GIS bakes a fixed pixel width into the iframe, so it cannot reflow on its
    // own — re-render it when the container's width actually changes.
    let lastWidth = Math.round(container.clientWidth);
    const observer = new ResizeObserver(() => {
      const width = Math.round(container.clientWidth);
      if (width === 0 || width === lastWidth) return;
      lastWidth = width;
      cleanup();
      cleanup = mount();
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      cleanup();
    };
  }, [isDark]);

  return (
    <div
      className={`auth-flow__google${disabled ? ' auth-flow__google--disabled' : ''}`}
      ref={containerRef}
    />
  );
}
